// NegotiationBriefPanel -- Lane 766, PHASE 4: the audit / edit surface.
//
// Ryan, twice, and this component is the answer to both:
//   "if they do go straight to the deck builder, then there needs to be a way
//    to audit it and edit it in there too."
//   "they see the deck and they want some things changed. How does that work?"
//
// WHAT WENT WRONG, AND WHY THIS IS THE FIX. On the AFT meeting the engine built
// the entire deck around a delivery-underperformance argument. The numbers were
// real; the argument was wrong, because XMEK had pushed those orders out
// themselves. Nobody could have caught it, because THE DECK NEVER SHOWED ITS
// CHOICE -- it just arrived, already committed. This panel shows the five
// arguments the deck can make, which ones are live, which the buyer has ruled
// out and why, and lets them change it in place.
//
// ⚠️ THE PRIMARY INTAKE IS A CLICK. This product has five free-text intakes
// holding five rows between them across seven months, none from the customer.
// So: pick a named lever (click), pick a canned first-person reason (click,
// which fills the field), save (click). Nothing here requires typing.
//
// ⚠️ THE REASON FIELD IS VISIBLE AND EDITABLE ANYWAY, AND THAT IS NOT A
// CONTRADICTION. What gets stored is quoted back later as the buyer's own
// words -- the chat renders it as `His words: "..."`. A sentence they never saw
// must never be attributed to them, so they always see it and can always change
// it. The notice above the field says exactly that. (The server records
// separately whether they chose the sentence or wrote it.)
//
// ⚠️ EVERYTHING ON THIS PANEL IS INTERNAL. Two of these entries would be
// actively harmful in front of a supplier: a kill reason hands them our
// rebuttal in writing, and the leverage posture is catastrophic. The banner is
// rendered from the SERVER's `internal_only`, which is derived from the store's
// audience map -- not hardcoded here, so it cannot go on claiming something the
// backend stopped guaranteeing.
//
// ⚠️ LANE 769 -- THE ONE SENTENCE A SUPPLIER READS IS NOT A DEFAULT. Measured
// on staging: the approve box opened PRE-FILLED with the buyer's own floated
// musing and the button was live the instant it was non-empty, so two clicks
// stored "Maybe we could suggest they route their shipments through our
// Singpaore facitily" as `offer_committed` -- hedge intact, typos intact, in his
// name, and rendered into the chat's prompt under "Use it as written". The box
// is still seeded (a blank field here means the offer simply never gets
// approved), but the button is DISABLED until the text changes, the store
// REFUSES the unchanged text regardless of what any client does, and the
// committed card now carries what he originally said beside what he approved.
//
// FLAG-DARK: the endpoint 404s when `companies.negotiation_brief_enabled` is
// FALSE (which is every tenant), and this panel then renders NOTHING -- so with
// the flag off the deck builder is byte-identical to before this lane.
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../../config';
import {
  fetchBriefAudit,
  promoteBriefOffer,
  retractBriefEntry,
  setBriefLever,
  setBriefLeverage,
} from '../../api/negotiationBrief';

const STATUS_LABEL = {
  live: 'In play',
  killed: 'Ruled out',
  promoted: 'Leading',
};

// The sentence shown above every reason field. It is the honesty contract for
// the whole surface, so it is stated once and reused rather than paraphrased.
const ATTRIBUTION_NOTICE =
  'This is stored as your reason and quoted back to you -- and to the '
  + 'assistant when it drafts -- as your words. Change it if that is not how '
  + 'you would put it.';

// Lane 769. Case-folded, whitespace-collapsed -- the SAME normalisation the
// store's `normalise_offer_text` applies, so the button this disables and the
// refusal the server would raise agree about what counts as an edit. A trailing
// space is not an edit by any honest reading.
function normaliseOffer(text) {
  return String(text || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

// The self-talk opener this draft begins with, or null. THE LIST COMES FROM THE
// SERVER (`offer_self_talk_openers`) and is never hardcoded here: a second copy
// in JavaScript would drift from the one the store records against.
//
// ⚠️ THIS NUDGES. IT NEVER BLOCKS. The list deliberately contains no soft
// outward ask -- "would you", "could we", "could you", "how about", "let's" are
// all absent, because each is a legitimate offer and several are the register
// the assistant is told to prefer when leverage is low. It only catches the
// buyer talking to himself.
function selfTalkOpener(text, openers) {
  const normalised = normaliseOffer(text);
  if (!normalised) return null;
  return (openers || []).find((o) => normalised.startsWith(String(o))) || null;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined,
    { day: 'numeric', month: 'short', year: 'numeric' });
}

// "said on 3 Aug 2026 by Ryan Kramer", with every absent part dropped. Never
// says "recently": an undated entry should read as undated, not as fresh.
function provenanceLine(entry) {
  if (!entry) return '';
  const bits = [];
  const when = fmtDate(entry.stated_on);
  if (when) bits.push(when);
  if (entry.stated_by) bits.push(entry.stated_by);
  return bits.join(' - ');
}

/**
 * The reason form. Presets are BUTTONS that fill the field; the field is always
 * visible so nothing is stored that the buyer has not read.
 */
function ReasonForm({ idPrefix, presets, value, onChange, presetKey,
  onPresetKey, onSave, onCancel, saving, saveLabel }) {
  const trimmed = (value || '').trim();
  return (
    <div className="dk-details" data-testid={`${idPrefix}-reason-form`}>
      <div className="dk-efl">Why? Pick one, or write your own.</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {(presets || []).map((p) => (
          <button
            key={p.preset_key}
            type="button"
            className="dk-btn"
            data-testid={`${idPrefix}-preset-${p.preset_key}`}
            aria-pressed={presetKey === p.preset_key}
            style={{ fontSize: '12px', padding: '4px 9px' }}
            onClick={() => { onChange(p.text); onPresetKey(p.preset_key); }}
          >
            {p.label || p.text}
          </button>
        ))}
      </div>

      {/* ABOVE the field, deliberately -- it changes what someone chooses to
          leave in it. Same placement, and the same reason, as the metric-caveat
          panel's audience notice. */}
      <div className="dk-savehint" style={{ marginTop: '10px', fontWeight: 600 }}>
        {ATTRIBUTION_NOTICE}
      </div>

      <textarea
        id={`${idPrefix}-reason`}
        className="dk-ta"
        style={{ marginTop: '6px' }}
        rows={3}
        maxLength={400}
        value={value}
        aria-label="Your reason"
        placeholder="Pick one above, or say it in your own words."
        onChange={(e) => onChange(e.target.value)}
      />

      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <button
          type="button"
          className="dk-btn"
          data-testid={`${idPrefix}-save`}
          disabled={!trimmed || saving}
          onClick={onSave}
        >
          {saving ? 'Saving...' : (saveLabel || 'Save')}
        </button>
        <button type="button" className="dk-btn"
          data-testid={`${idPrefix}-cancel`} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function NegotiationBriefPanel({
  supplierId,
  supplierName,
  onChanged,
  apiBase = API_BASE,
  token = null,
}) {
  const [data, setData] = useState(null);      // null = dark / unavailable
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);

  // One open form at a time: {kind:'lever'|'leverage'|'offer', key, action}.
  const [form, setForm] = useState(null);
  const [reason, setReason] = useState('');
  const [presetKey, setPresetKey] = useState(null);
  const [posture, setPosture] = useState(null);
  const [offerText, setOfferText] = useState('');
  // Lane 769. What the panel PUT in the box ('floated' | null) -- sent as a
  // pointer so the stored provenance can tell an edit of his own words from a
  // sentence written on a blank field. And the one-shot acknowledgement of the
  // self-talk nudge, which resets whenever the text changes so it can never
  // carry over onto a sentence the buyer has not re-read.
  const [offerSeed, setOfferSeed] = useState(null);
  const [selfTalkAcked, setSelfTalkAcked] = useState(false);

  const opts = { apiBase, token };

  const load = useCallback(async () => {
    if (!supplierId) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      // 404 -> null -> render nothing. Flag off and "not this tenant's
      // supplier" are the SAME response on purpose (no existence enumeration
      // across tenants), so the panel treats both as "there is nothing here".
      setData(await fetchBriefAudit(supplierId, opts));
    } catch (e) {
      setData(null);
      setError(e.message || 'Could not load the brief.');
    } finally {
      setLoading(false);
    }
  }, [supplierId, apiBase, token]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const closeForm = () => {
    setForm(null); setReason(''); setPresetKey(null);
    setPosture(null); setOfferText('');
    setOfferSeed(null); setSelfTalkAcked(false);
  };

  // Every edit of the approved sentence retires the nudge acknowledgement. A
  // buyer who acknowledged "this opens like a note to yourself" about one
  // sentence has not acknowledged it about the next one.
  const changeOfferText = (next) => {
    setOfferText(next);
    setSelfTalkAcked(false);
  };

  // EVERY mutation replaces state with the server's own re-read (Rule 111), and
  // then tells the assembler the deck is out of date. The buyer never has to
  // work out whether the panel and the deck now agree -- that guessing is the
  // exact thing this surface exists to remove.
  const mutate = async (fn) => {
    setSaving(true);
    setError(null);
    try {
      setData(await fn());
      setChanged(true);
      closeForm();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message || 'That did not save.');
    } finally {
      setSaving(false);
    }
  };

  if (!supplierId) {
    return (
      <div className="dk-inspect-empty" data-testid="brief-needs-subject">
        Pick a subject supplier to see what this deck will argue.
      </div>
    );
  }
  if (loading && !data) {
    return <div className="dk-loading" data-testid="brief-loading">Loading...</div>;
  }
  // Dark, or unavailable. Render NOTHING rather than an error -- a feature that
  // is off should be invisible, not broken-looking.
  if (!data) return null;

  const killPresets = data.kill_reason_presets || [];
  const promotePresets = data.promote_reason_presets || [];
  const postures = data.posture_catalogue || [];
  const activePosture = postures.find((p) => p.posture === posture) || null;

  const openLeverForm = (leverKey, action) => {
    setForm({ kind: 'lever', key: leverKey, action });
    setReason(''); setPresetKey(null);
  };

  const saveLever = () => mutate(() => setBriefLever({
    supplierId,
    leverKey: form.key,
    action: form.action,
    reason: reason.trim(),
    presetKey,
  }, opts));

  const saveLeverage = () => mutate(() => setBriefLeverage({
    supplierId, posture, reason: reason.trim(), presetKey,
  }, opts));

  const saveOffer = () => mutate(() => promoteBriefOffer({
    supplierId,
    floatedAssertionId: form.key,
    approvedText: offerText.trim(),
    offerSeed,
  }, opts));

  const undoLever = (leverKey, predicate) => mutate(() => retractBriefEntry({
    supplierId, leverKey, predicate,
  }, opts));

  // ⚠️ THE SECOND RETRACT PATH, AND IT IS NOT INTERCHANGEABLE WITH THE FIRST.
  // The store REFUSES to retract by key when the key is null, because unmapped
  // kills are distinct statements that merely share a missing key -- sweeping
  // them together would undo all of them because the buyer undid one. So every
  // control that acts on a single row (an unmapped kill, a floated offer, a
  // posture, a committed offer) goes through the id path instead.
  const undoById = (assertionId) => mutate(() => retractBriefEntry({
    supplierId, assertionId,
  }, opts));

  return (
    <div className="dk-stack" data-testid="brief-panel">
      {data.internal_only ? (
        <div className="dk-locknote" data-testid="brief-internal-banner"
          style={{ marginTop: 0 }}>
          <span className="dk-internal-chip">Internal only</span>{' '}
          Nothing on this page goes to {supplierName || 'the supplier'}. It is
          how we decide what to argue, not what we send.
        </div>
      ) : null}

      {/* Lane 789: `dk-arg-lead` is a PRESENTATION HOOK ONLY -- it lets the
          argument line span this heading across the full-width grid it now
          renders this panel into. No behaviour, no content, no control moved. */}
      <div className="dk-arg-lead">
        <div className="dk-ins-h" style={{ marginBottom: '4px' }}>
          What this deck will argue
        </div>
        <div className="dk-savehint">
          These are every argument the deck can make about{' '}
          {supplierName || 'this supplier'}. Rule one out and it leaves the
          deck, the prep pack and anything the assistant drafts.
        </div>
      </div>

      {/* ⚠️ DISCLOSED, NOT SILENT (Lane 801). Bringing back one name also brings
          back its twin, because the two name one argument and the deck cannot
          tell them apart. That is the only outcome here the buyer could not have
          predicted from the button he pressed, so it is said out loud. Absent on
          every ordinary retract -- the server sends an empty list -- and it
          clears on the next read, because the GET payload has no such key. */}
      {(data.retracted_also || []).length ? (
        <div className="dk-savehint" data-testid="brief-retracted-also">
          That also brought back{' '}
          {(data.retracted_also || [])
            .map((k) => (data.levers || []).find((l) => l.lever_key === k))
            .filter(Boolean)
            .map((l) => `"${l.label}"`)
            .join(', ')}
          {' '}- the same argument under its other name.
        </div>
      ) : null}

      {(data.levers || []).map((lever) => {
        const entry = lever.killed || lever.promoted || null;
        // The sibling lever this row's state came from, when it is not its own.
        const viaKey = lever.killed_via || lever.promoted_via || null;
        const inheritedFrom = viaKey
          ? (data.levers || []).find((l) => l.lever_key === viaKey) || null
          : null;
        const isKilled = lever.status === 'killed';
        const isPromoted = lever.status === 'promoted';
        const formOpen = form && form.kind === 'lever'
          && form.key === lever.lever_key;
        return (
          <div key={lever.lever_key} className="dk-details"
            data-testid={`brief-lever-${lever.lever_key}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              gap: '10px', alignItems: 'baseline' }}>
              <strong style={{ fontSize: '13px' }}>{lever.label}</strong>
              <span className="dk-pill"
                data-testid={`brief-status-${lever.lever_key}`}>
                {STATUS_LABEL[lever.status] || lever.status}
              </span>
            </div>

            <div className="dk-savehint" style={{ marginTop: '4px' }}>
              {lever.what_it_argues}
            </div>
            <div className="dk-savehint">{lever.where_it_shows}</div>

            {/* Honest or absent, never plausible: a made-up figure here would
                quietly settle the very argument the buyer opened this to
                settle. */}
            <div className="dk-savehint" style={{ marginTop: '4px' }}
              data-testid={`brief-evidence-${lever.lever_key}`}>
              {lever.evidence || lever.evidence_note}
            </div>

            {entry ? (
              <div className="dk-locknote" data-testid={`brief-entry-${lever.lever_key}`}>
                {/* INHERITED FROM THE OTHER NAME (Lane 801). Two levers can name
                    the same argument -- one for the argument, one for the page
                    that carries it -- and killing either removes the same things
                    from the deck. When the state came from the sibling, say so:
                    otherwise his sentence reads as though he said it about THIS
                    row, which is the wrongly-attributed-statement class. */}
                {inheritedFrom ? (
                  <div className="dk-savehint" style={{ marginBottom: '4px' }}
                    data-testid={`brief-inherited-${lever.lever_key}`}>
                    You ruled this out as &ldquo;{inheritedFrom.label}&rdquo; -
                    it is the same argument, so this one is closed too.
                  </div>
                ) : null}
                {/* The buyer's own sentence, in quotes because it IS a quote.
                    `content` is our description of the action and is never
                    quoted -- the two are kept apart all the way from storage. */}
                {entry.reason_verbatim
                  ? <div style={{ fontStyle: 'italic' }}>&ldquo;{entry.reason_verbatim}&rdquo;</div>
                  : <div>{entry.content}</div>}
                <div className="dk-savehint" style={{ marginTop: '4px' }}>
                  {isKilled ? 'Ruled out' : 'Promoted'}
                  {provenanceLine(entry) ? ` - ${provenanceLine(entry)}` : ''}
                  {entry.expires_at
                    ? `. Lapses ${fmtDate(entry.expires_at)} unless you say it again.`
                    : ''}
                </div>
              </div>
            ) : null}

            {formOpen ? (
              <ReasonForm
                idPrefix={`brief-lever-${lever.lever_key}`}
                presets={form.action === 'kill' ? killPresets : promotePresets}
                value={reason}
                onChange={(v) => { setReason(v); }}
                presetKey={presetKey}
                onPresetKey={setPresetKey}
                onSave={saveLever}
                onCancel={closeForm}
                saving={saving}
                saveLabel={form.action === 'kill'
                  ? 'Rule this out' : 'Lead with this'}
              />
            ) : (
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px',
                flexWrap: 'wrap' }}>
                {isKilled ? (
                  <button type="button" className="dk-btn" disabled={saving}
                    data-testid={`brief-unkill-${lever.lever_key}`}
                    onClick={() => undoLever(lever.lever_key, 'lever_killed')}>
                    Bring this back
                  </button>
                ) : (
                  <button type="button" className="dk-btn" disabled={saving}
                    data-testid={`brief-kill-${lever.lever_key}`}
                    onClick={() => openLeverForm(lever.lever_key, 'kill')}>
                    Rule this out
                  </button>
                )}
                {isPromoted ? (
                  <button type="button" className="dk-btn" disabled={saving}
                    data-testid={`brief-unpromote-${lever.lever_key}`}
                    onClick={() => undoLever(lever.lever_key, 'lever_promoted')}>
                    Stop leading with this
                  </button>
                ) : (
                  <button type="button" className="dk-btn" disabled={saving}
                    data-testid={`brief-promote-${lever.lever_key}`}
                    onClick={() => openLeverForm(lever.lever_key, 'promote')}>
                    Lead with this
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* ⚠️ VISIBLE, NEVER SILENTLY DROPPED. The extractor could not map these
          to an argument we make, so they suppress NOTHING -- guessing would be
          worse than the gap. Showing them is how the buyer finds out we heard
          them but could not act on it. */}
      {(data.unmapped_kills || []).length ? (
        <div className="dk-details" data-testid="brief-unmapped">
          <div className="dk-ins-h" style={{ marginBottom: '4px' }}>
            You ruled something out that I could not match to an argument
          </div>
          <div className="dk-savehint">
            These are not removing anything from the deck -- I could not tell
            which argument you meant.
          </div>
          {data.unmapped_kills.map((entry) => (
            <div key={entry.assertion_id} className="dk-locknote"
              data-testid={`brief-unmapped-${entry.assertion_id}`}>
              {entry.reason_verbatim
                ? <div style={{ fontStyle: 'italic' }}>&ldquo;{entry.reason_verbatim}&rdquo;</div>
                : <div>{entry.content}</div>}
              <div className="dk-savehint" style={{ marginTop: '4px' }}>
                {provenanceLine(entry)}
              </div>
              <button type="button" className="dk-btn"
                style={{ marginTop: '8px' }} disabled={saving}
                data-testid={`brief-unmapped-undo-${entry.assertion_id}`}
                onClick={() => undoById(entry.assertion_id)}>
                Remove this note
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {/* HOW HARD WE CAN PUSH. Ryan: "we don't have much leverage, the customer
          directs this spend." Killing a lever removes a page; this rewrites
          every page -- and it never expires, because it is still true in three
          months. */}
      <div className="dk-details" data-testid="brief-leverage">
        <div className="dk-ins-h" style={{ marginBottom: '4px' }}>
          How hard can we push these people?
        </div>
        {data.leverage ? (
          <div className="dk-locknote" data-testid="brief-leverage-current">
            <strong>
              {(postures.find((p) => p.posture === data.leverage.posture) || {})
                .label || data.leverage.posture}
            </strong>
            {data.leverage.reason_verbatim ? (
              <div style={{ fontStyle: 'italic', marginTop: '4px' }}>
                &ldquo;{data.leverage.reason_verbatim}&rdquo;
              </div>
            ) : null}
            <div className="dk-savehint" style={{ marginTop: '4px' }}>
              {provenanceLine(data.leverage)}
              {/* Age is always shown. A posture never expires, so a two-year-old
                  one must read as visibly old rather than silently current. */}
              {typeof data.leverage.age_days === 'number'
                ? ` - set ${data.leverage.age_days} day${data.leverage.age_days === 1 ? '' : 's'} ago`
                : ''}
            </div>
            <button type="button" className="dk-btn" style={{ marginTop: '8px' }}
              disabled={saving} data-testid="brief-leverage-clear"
              onClick={() => undoById(data.leverage.assertion_id)}>
              Clear this
            </button>
          </div>
        ) : (
          <div className="dk-savehint">
            Not set. The deck and the assistant will use their normal register.
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px',
          marginTop: '8px' }}>
          {postures.map((p) => (
            <button key={p.posture} type="button" className="dk-btn"
              disabled={saving}
              aria-pressed={posture === p.posture}
              data-testid={`brief-posture-${p.posture}`}
              style={{ fontSize: '12px', padding: '4px 9px' }}
              onClick={() => {
                setForm({ kind: 'leverage' });
                setPosture(p.posture);
                setReason(''); setPresetKey(null);
              }}>
              {p.label}
            </button>
          ))}
        </div>

        {form && form.kind === 'leverage' && activePosture ? (
          <>
            <div className="dk-savehint" style={{ marginTop: '8px' }}>
              {activePosture.what_it_changes}
            </div>
            <ReasonForm
              idPrefix="brief-leverage"
              presets={(activePosture.reason_presets || []).map((p) => ({
                ...p, label: p.text,
              }))}
              value={reason}
              onChange={setReason}
              presetKey={presetKey}
              onPresetKey={setPresetKey}
              onSave={saveLeverage}
              onCancel={closeForm}
              saving={saving}
              saveLabel="Set our position"
            />
          </>
        ) : null}
      </div>

      {/* OFFERS. A floated offer is the buyer MUSING. It is internal until they
          approve the literal sentence -- turning "maybe they could route
          through Singapore" into "We propose routing through Singapore" would
          put a commitment in their mouth they never made. */}
      {((data.offers || {}).floated || []).length
        || ((data.offers || {}).committed || []).length ? (
        <div className="dk-details" data-testid="brief-offers">
          <div className="dk-ins-h" style={{ marginBottom: '4px' }}>
            Things you have floated
          </div>

          {((data.offers || {}).floated || []).map((offer) => {
            // Lane 769, computed per offer because both drive THIS card's
            // button. `unchanged` is the same normalised comparison the store
            // makes, so the disabled button and the server's refusal agree.
            const draft = offerText.trim();
            const editingThis = !!(form && form.kind === 'offer'
              && form.key === offer.assertion_id);
            const unchanged = editingThis && !!draft
              && normaliseOffer(draft) === normaliseOffer(offer.offer_verbatim);
            const opener = editingThis
              ? selfTalkOpener(draft, data.offer_self_talk_openers)
              : null;
            // Lane 777. FROM THE SERVER, like the openers above -- a second
            // copy of the number here would drift from the store that enforces
            // it, and the buyer would fill a box only to be refused on click.
            // The fallback matches the store's own default so a payload from an
            // older backend still bounds the box rather than unbounding it.
            const offerLimit = data.max_approved_offer_chars || 600;
            const offerLeft = offerLimit - draft.length;
            return (
            <div key={offer.assertion_id} className="dk-locknote"
              data-testid={`brief-offer-${offer.assertion_id}`}>
              <span className="dk-internal-chip">Not sent</span>
              <div className="dk-efl">You said</div>
              <div style={{ fontStyle: 'italic', marginTop: '2px' }}>
                &ldquo;{offer.offer_verbatim || offer.content}&rdquo;
              </div>
              <div className="dk-savehint" style={{ marginTop: '4px' }}>
                {provenanceLine(offer)}
              </div>

              {form && form.kind === 'offer'
                && form.key === offer.assertion_id ? (
                <div style={{ marginTop: '8px' }}>
                  <div className="dk-efl">
                    They will read
                  </div>
                  <div className="dk-savehint" style={{ fontWeight: 600 }}>
                    This is the only thing on this page they will ever see, and
                    it goes out exactly as written. A summary of an offer is not
                    the offer.
                  </div>
                  <textarea className="dk-ta" rows={3} maxLength={offerLimit}
                    style={{ marginTop: '6px' }}
                    aria-label="The sentence the supplier will read"
                    data-testid={`brief-offer-text-${offer.assertion_id}`}
                    value={offerText}
                    onChange={(e) => changeOfferText(e.target.value)} />

                  {/* ⚠️ LANE 777 -- THE LIMIT IS SHOWN WHERE HE IS TYPING.
                      This sentence is handed to the assistant under "use it as
                      written", so it has to arrive whole. Before this lane the
                      box accepted 2,000 characters and the prompt carried the
                      first 237 plus an ellipsis -- and NOTHING said so: this
                      card showed him the whole sentence he had approved while
                      the model was told to reproduce a fragment of it verbatim.
                      A limit the buyer can see is a constraint he can work
                      with; the same limit applied where the model receives it
                      is silent data loss. */}
                  <div className="dk-savehint" style={{ marginTop: '4px' }}
                    data-testid={`brief-offer-count-${offer.assertion_id}`}>
                    {offerLeft >= 0
                      ? `${offerLeft} characters left of ${offerLimit}.`
                      : `${-offerLeft} characters over the ${offerLimit} limit.`}
                    {' '}This goes over word for word, so it has to fit whole.
                  </div>

                  {/* ⚠️ LANE 769 -- THE MEASURED DEFECT, MADE UNREACHABLE HERE.
                      What he floated is a note to himself; it was approved
                      unchanged in two clicks and reached the one supplier-facing
                      row in the system with its hedge and its typos intact. The
                      server refuses the unchanged text outright; this disables
                      the button first so he sees WHY rather than meeting a
                      refusal after committing to it. */}
                  {unchanged ? (
                    <div className="dk-savehint" style={{ marginTop: '6px' }}
                      data-testid={`brief-offer-unchanged-${offer.assertion_id}`}>
                      This is still word for word what you said to yourself.
                      Turn it into the sentence you want
                      {' '}{supplierName || 'them'} to read.
                    </div>
                  ) : null}

                  {/* A NUDGE, NOT A GATE. Soft outward asks ("would you...",
                      "could you share...") are absent from the server's list on
                      purpose -- they are legitimate offers, and the preferred
                      register when leverage is low. This only catches a
                      sentence that still opens by musing. */}
                  {opener && !unchanged ? (
                    <div className="dk-savehint" style={{ marginTop: '6px',
                      fontWeight: 600 }}
                      data-testid={`brief-offer-selftalk-${offer.assertion_id}`}>
                      This still opens like a note to yourself
                      (&ldquo;{opener}&rdquo;).
                      {' '}{supplierName || 'They'} will read it exactly as
                      written.
                      {selfTalkAcked ? '' : ' Click approve again to send it anyway.'}
                    </div>
                  ) : null}

                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px',
                    flexWrap: 'wrap' }}>
                    <button type="button" className="dk-btn"
                      disabled={!offerText.trim() || unchanged || saving}
                      data-testid={`brief-offer-save-${offer.assertion_id}`}
                      onClick={() => {
                        // The one-shot confirm. It CANNOT lock anyone out --
                        // the second click always saves.
                        if (opener && !selfTalkAcked) {
                          setSelfTalkAcked(true);
                          return;
                        }
                        saveOffer();
                      }}>
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {saving ? 'Saving...' : (opener && selfTalkAcked
                        ? 'Send it exactly like this'
                        : 'Approve this wording')}
                    </button>
                    <button type="button" className="dk-btn"
                      data-testid={`brief-offer-blank-${offer.assertion_id}`}
                      onClick={() => {
                        // Starting over drops the seed pointer, so what gets
                        // stored says he wrote this rather than edited ours.
                        setOfferText(''); setOfferSeed(null);
                        setSelfTalkAcked(false);
                      }}>
                      Start from blank
                    </button>
                    <button type="button" className="dk-btn"
                      data-testid={`brief-offer-cancel-${offer.assertion_id}`}
                      onClick={closeForm}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px',
                  flexWrap: 'wrap' }}>
                  <button type="button" className="dk-btn" disabled={saving}
                    data-testid={`brief-offer-approve-${offer.assertion_id}`}
                    onClick={() => {
                      setForm({ kind: 'offer', key: offer.assertion_id });
                      // Seeded with their own words so approving is an EDIT,
                      // not a blank page -- but the save button stays DISABLED
                      // until it changes, because the hedge is the part that
                      // must be removed knowingly. Seeding a blank field was
                      // considered and rejected: this product's free-text
                      // intakes hold almost nothing, and a blank box here means
                      // the offer simply never gets approved.
                      setOfferText(offer.offer_verbatim || '');
                      setOfferSeed('floated');
                      setSelfTalkAcked(false);
                    }}>
                    Approve wording for {supplierName || 'the supplier'}
                  </button>
                  <button type="button" className="dk-btn" disabled={saving}
                    data-testid={`brief-offer-drop-${offer.assertion_id}`}
                    onClick={() => undoById(offer.assertion_id)}>
                    Drop this
                  </button>
                </div>
              )}
            </div>
            );
          })}

          {((data.offers || {}).committed || []).map((offer) => (
            <div key={offer.assertion_id} className="dk-locknote"
              data-testid={`brief-committed-${offer.assertion_id}`}>
              <span className="dk-pill indeck">Approved to send</span>
              <div className="dk-efl" style={{ marginTop: '6px' }}>
                They will read
              </div>
              <div style={{ marginTop: '2px' }}>
                {offer.offer_text || offer.content}
              </div>

              {/* ⚠️ LANE 769. Promotion RETIRES the floated row and every brief
                  read filters on `valid_until IS NULL`, so the moment an offer
                  is approved the words he actually said stop being visible
                  anywhere on this panel. The committed row carries them itself
                  now. This is the internal half of the record and it is why the
                  approved sentence can be checked against its origin at all. */}
              {offer.floated_verbatim ? (
                <div style={{ marginTop: '8px' }}
                  data-testid={`brief-committed-floated-${offer.assertion_id}`}>
                  <div className="dk-efl">You had said</div>
                  <div style={{ fontStyle: 'italic', marginTop: '2px' }}>
                    &ldquo;{offer.floated_verbatim}&rdquo;
                  </div>
                </div>
              ) : null}

              <div className="dk-savehint" style={{ marginTop: '4px' }}>
                Approved by {offer.approved_by || 'someone on your team'}
                {fmtDate(offer.stated_on) ? ` - ${fmtDate(offer.stated_on)}` : ''}
                {offer.offer_source === 'typed' ? ' - written here' : ''}
                {offer.offer_source === 'edited_floated'
                  ? ' - edited from what you said' : ''}
              </div>
              <button type="button" className="dk-btn" style={{ marginTop: '8px' }}
                disabled={saving}
                data-testid={`brief-committed-withdraw-${offer.assertion_id}`}
                onClick={() => undoById(offer.assertion_id)}>
                Withdraw this
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {error ? (
        <div className="dk-notice-fallback" data-testid="brief-error">{error}</div>
      ) : null}

      {/* THE ANSWER TO "they want some things changed -- how does that work?"
          A change here does not silently rewrite a deck that is already on
          screen. It marks the preview out of date (the assembler's existing
          staleness signature), and Refresh preview rebuilds it -- the same
          control that already handles every other edit in this builder. */}
      {changed ? (
        <div className="dk-savehint" data-testid="brief-stale-hint"
          style={{ fontWeight: 600 }}>
          Saved. The preview is now out of date -- click Refresh preview to
          rebuild the deck with this.
        </div>
      ) : null}
    </div>
  );
}

export default NegotiationBriefPanel;
